import axios, { type InternalAxiosRequestConfig } from 'axios'
import { apiBaseUrl } from '@/config/api'
import router from '@/router'
import { ErrorCode } from '@/errors/codes'
import { errorPathForCode } from '@/errors/code_to_error_route'
import { dedicatedErrorCodeFromBackend401Payload } from '@/errors/backend_401_error_code'
import { dedicatedErrorCodeFromBackend403Payload } from '@/errors/backend_403_error_code'
import { HeaderCookieName } from '@/constants/cookies'
import { getCookie } from '@/utils/getCookie'

/**
 * Opt-out flag for the global 401 / network-error redirect to a dedicated error page.
 *
 * Default (flag absent or `false`): the response interceptor pushes the router to the right
 * error view on auth failures, so individual call sites do not need to know about routing.
 *
 * Set to `true` for "soft" probes where 401 is an expected, non-error answer that simply means
 * "no session yet" — e.g. the Welcome tab calling `GET /auth/me` to detect whether the user is
 * already signed in. The interceptor still rejects the promise so the caller can react, but it
 * will not navigate. Has no effect on 403.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean
  }
  export interface InternalAxiosRequestConfig {
    skipAuthRedirect?: boolean
  }
}

/**
 * The UI (Vite) and the API often run on different origins (e.g. `localhost:5173` vs `localhost:8000`).
 * For those “cross-origin” requests, browsers default to **not** including cookies and **not** exposing
 * responses that set cookies, unless the client opts in.
 *
 * `withCredentials: true` tells the browser to:
 * - Send cookies the API host already has (same-site rules still apply).
 * - Accept `Set-Cookie` on responses from the API so HttpOnly session cookies can be stored for that host.
 *
 * The backend must allow this: CORS with `Access-Control-Allow-Credentials: true` and a concrete
 * `Access-Control-Allow-Origin` (not `*`) matching the SPA origin. Without that, the preflight or response
 * may fail and no cookies will appear for the API origin in DevTools.
 */
const http = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
})

/**
 * Request interceptor: double-submit CSRF.
 *
 * The server sets a readable `XSRF-TOKEN` cookie; we mirror its value into the
 * `X-CSRF-TOKEN` request header. The server validates that both match, which
 * proves the request came from our origin (an attacker’s site cannot read the
 * cookie value cross-origin, so it cannot forge the header).
 *
 * Do NOT use `XAUTH-DATA` here: that cookie is normally HttpOnly, so it never
 * appears in `document.cookie` and the header would never be set.
 */
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const xsrf = getCookie(HeaderCookieName.XSRF_TOKEN_COOKIE)
  if (xsrf) {
    config.headers.set('X-CSRF-TOKEN', xsrf)
  }
  return config
})

function extractErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') {
    return undefined
  }

  const payload = data as Record<string, unknown>
  const errorMessage = payload.error_message
  if (typeof errorMessage === 'string' && errorMessage.trim().length > 0) {
    return errorMessage.trim()
  }
  const details = payload.details
  if (typeof details === 'string' && details.trim().length > 0) {
    return details
  }
  return undefined
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    // When the server returns 401 without CORS headers the browser blocks the
    // response body and axios reports a generic ERR_NETWORK. Until the backend
    // includes CORS headers on 401 responses, we treat network errors as
    // potential auth failures and redirect to the login/unauthorized page.
    const isNetworkError = !error.response && error.code === 'ERR_NETWORK'
    const skipAuthRedirect = error.config?.skipAuthRedirect === true
    if ((status === 401 || isNetworkError) && !skipAuthRedirect) {
      const payload = error.response?.data
      const message = extractErrorMessage(payload)
      const backendCode =
        payload && typeof payload === 'object' && 'error_code' in payload
          ? (payload as Record<string, unknown>).error_code
          : undefined
      const dedicated = dedicatedErrorCodeFromBackend401Payload(backendCode)

      if (dedicated) {
        router.push({
          path: errorPathForCode(dedicated),
          query: message ? { errorMessage: message } : undefined,
        })
      } else {
        router.push({
          path: errorPathForCode(ErrorCode.Unauthorized),
          query: message ? { backendMessage: message } : undefined,
        })
      }
    }

    if (status === 403) {
      const payload = error.response?.data
      const message = extractErrorMessage(payload)
      const backendCode =
        payload && typeof payload === 'object' && 'error_code' in payload
          ? (payload as Record<string, unknown>).error_code
          : undefined
      const dedicated = dedicatedErrorCodeFromBackend403Payload(backendCode)
      if (dedicated) {
        router.push({
          path: errorPathForCode(dedicated),
          query: message ? { errorMessage: message } : undefined,
        })
      }
    }
    return Promise.reject(error)
  },
)

export default http
